/**
 * Shared Navigation Component - V2 Reorganized
 * Desktop: Top nav with grouped dropdown menus
 * Mobile: Bottom tab bar with organized "More" sheet
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, RefreshCw, Briefcase, Layers, Bitcoin, Bot, Award,
  Settings, LogOut, Home, X, Newspaper, Timer, MoreHorizontal,
  User, Bell, DollarSign, Target, Compass, BookOpen, BarChart3, Activity,
  Search, ChevronDown, LineChart, Zap, Brain
} from 'lucide-react';

// ─── Navigation Structure ───────────────────────────────────────
const NAV_STRUCTURE = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
    path: '/',
    color: 'text-accent-blue',
    type: 'link',
  },
  {
    id: 'trading',
    label: 'Trading',
    icon: TrendingUp,
    color: 'text-accent-green',
    type: 'dropdown',
    children: [
      { path: '/portfolio', label: 'Portfolio', icon: Briefcase, desc: 'Holdings & balances' },
      { path: '/positions', label: 'Positions', icon: Target, desc: 'Active position management' },
      { path: '/stocks', label: 'All Stocks', icon: Search, desc: 'Browse all tradable assets' },
      { path: '/options', label: 'Options', icon: Layers, desc: 'Options trading' },
      { path: '/crypto', label: 'Crypto', icon: Bitcoin, desc: 'Cryptocurrency markets' },
    ],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    icon: BarChart3,
    color: 'text-accent-purple',
    type: 'dropdown',
    children: [
      { path: '/scanner', label: 'Stock Scanner', icon: Search, desc: 'Scan for opportunities' },
      { path: '/strategies', label: 'Strategies', icon: Compass, desc: 'Strategy templates' },
      { path: '/backtest', label: 'Backtest', icon: LineChart, desc: 'Test strategies historically' },
      { path: '/performance', label: 'Performance', icon: Activity, desc: 'Track your results' },
      { path: "/scorecard", label: "AI Scorecard", icon: Brain, desc: "Daily performance reports" },
      { path: '/agents', label: 'AI Agents', icon: Zap, desc: 'Agent team, screening, backtesting' },
      { path: '/war-room', label: 'War Room', icon: Brain, desc: 'Two AI firms deliberate; Arbiter decides' },
    ],
  },
  {
    id: 'research',
    label: 'Research',
    icon: Newspaper,
    color: 'text-accent-yellow',
    type: 'dropdown',
    children: [
      { path: '/news', label: 'News Feed', icon: Newspaper, desc: 'Market news & events' },
      { path: '/gurus', label: 'Gurus', icon: Award, desc: 'Expert insights' },
      { path: '/leaderboard', label: 'Leaderboard', icon: Award, desc: 'Top performers' },
    ],
  },
  {
    id: 'bot',
    label: 'Bot',
    icon: Bot,
    path: '/admin',
    color: 'text-accent-purple',
    type: 'link',
  },
  {
    id: 'journal',
    label: 'Journal',
    icon: BookOpen,
    path: '/journal',
    color: 'text-accent-green',
    type: 'link',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings',
    color: 'text-text-secondary',
    type: 'link',
  },
  {
    id: 'platform-admin',
    label: 'Admin',
    icon: Zap,
    path: '/platform-admin',
    color: 'text-accent-orange',
    type: 'link',
  },
];

// Mobile bottom bar items
const MOBILE_PRIMARY = [
  { path: '/', label: 'Home', icon: Home, color: 'text-accent-blue' },
  { path: '/portfolio', label: 'Portfolio', icon: Briefcase, color: 'text-accent-green' },
  { path: '/crypto', label: 'Crypto', icon: Bitcoin, color: 'text-accent-yellow' },
  { path: '/admin', label: 'Bot', icon: Bot, color: 'text-accent-purple' },
];

// Mobile "More" organized sections
const MOBILE_SECTIONS = [
  {
    title: 'Trading',
    items: [
      { path: '/positions', label: 'Positions', icon: Target, color: 'text-accent-blue' },
      { path: '/stocks', label: 'All Stocks', icon: Search, color: 'text-accent-green' },
      { path: '/options', label: 'Options', icon: Layers, color: 'text-accent-orange' },
    ],
  },
  {
    title: 'Analysis',
    items: [
      { path: '/scanner', label: 'Stock Scanner', icon: Search, color: 'text-accent-yellow' },
      { path: '/strategies', label: 'Strategies', icon: Compass, color: 'text-accent-purple' },
      { path: '/backtest', label: 'Backtest', icon: LineChart, color: 'text-accent-blue' },
      { path: '/performance', label: 'Performance', icon: Activity, color: 'text-accent-green' },
      { path: "/scorecard", label: "AI Scorecard", icon: Brain, color: "text-accent-purple" },
      { path: '/agents', label: 'AI Agents', icon: Zap, color: 'text-accent-blue' },
    ],
  },
  {
    title: 'Research',
    items: [
      { path: '/news', label: 'News Feed', icon: Newspaper, color: 'text-accent-green' },
      { path: '/gurus', label: 'Gurus', icon: Award, color: 'text-accent-orange' },
      { path: '/leaderboard', label: 'Leaderboard', icon: Award, color: 'text-accent-yellow' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { path: '/journal', label: 'Trade Journal', icon: BookOpen, color: 'text-accent-green' },
      { path: '/settings', label: 'Settings', icon: Settings, color: 'text-text-secondary' },
      { path: '/platform-admin', label: 'Platform Admin', icon: Zap, color: 'text-accent-orange' },
    ],
  },
];

// ─── Dropdown Component ─────────────────────────────────────────
const NavDropdown = ({ item, isAnyChildActive }) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 200);
  };

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger */}
      <button
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all text-sm font-medium ${
          isAnyChildActive
            ? `bg-bg-tertiary ${item.color}`
            : 'text-text-muted hover:text-text-primary hover:bg-bg-tertiary/50'
        }`}
        onClick={() => setOpen(!open)}
      >
        <item.icon className="w-4 h-4" />
        <span>{item.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className="absolute top-full left-0 mt-1 w-56 py-1.5 bg-bg-secondary border border-border-color rounded-xl shadow-2xl shadow-black/40 z-50"
          style={{ animation: 'navFadeIn 0.15s ease-out' }}
        >
          {item.children.map((child) => {
            const childActive = location.pathname === child.path;
            return (
              <Link
                key={child.path}
                to={child.path}
                className={`flex items-center gap-3 px-3.5 py-2.5 mx-1.5 rounded-lg transition-all ${
                  childActive
                    ? 'bg-bg-tertiary text-text-primary'
                    : 'text-text-secondary hover:bg-bg-tertiary/60 hover:text-text-primary'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  childActive ? 'bg-bg-primary' : 'bg-bg-tertiary/50'
                }`}>
                  <child.icon className={`w-4 h-4 ${childActive ? item.color : 'text-text-muted'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium leading-tight">{child.label}</div>
                  <div className="text-[11px] text-text-muted leading-tight mt-0.5 truncate">{child.desc}</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Navigation ────────────────────────────────────────────
const Navigation = ({
  onRefresh,
  refreshing,
  marketClock,
  connectionStatus,
  autoRefreshEnabled,
  autoRefreshCountdown,
  onToggleAutoRefresh,
  onShowConnectionModal,
  children,
}) => {
  const { logout, hasAlpacaKeys, user } = useAuth();
  const location = useLocation();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const isActive = (path) =>
    location.pathname === path || (path === '/' && location.pathname === '/dashboard');

  const isChildActive = (item) =>
    item.children?.some((c) => isActive(c.path));

  // Get current page name for mobile header
  const getCurrentPageLabel = () => {
    for (const item of NAV_STRUCTURE) {
      if (item.path && isActive(item.path)) return item.label;
      if (item.children) {
        const child = item.children.find((c) => isActive(c.path));
        if (child) return child.label;
      }
    }
    return 'Paeru';
  };

  // Global navigation now lives in <AppShell> (wraps every route). This legacy top-nav is
  // retired — render only any children passed to it, no nav bar. (Body below is dead code.)
  return children || null;

  /* eslint-disable no-unreachable */
  return (
    <>
      {/* Dropdown animation keyframe */}
      <style>{`
        @keyframes navFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ============ DESKTOP HEADER ============ */}
      <header className="hidden md:block sticky top-0 z-40 bg-bg-primary/80 backdrop-blur-xl border-b border-border-color">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
          <div className="flex items-center h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 mr-6 shrink-0">
              <div className="w-9 h-9 rounded-xl gradient-bg-blue flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-display text-lg font-bold gradient-text-blue hidden lg:block">
                Paeru Exchange
              </h1>
            </Link>

            {/* Desktop Navigation - Grouped */}
            <nav className="flex items-center gap-0.5 flex-1">
              {NAV_STRUCTURE.map((item) => {
                if (item.type === 'link') {
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all text-sm font-medium ${
                        isActive(item.path)
                          ? `bg-bg-tertiary ${item.color}`
                          : 'text-text-muted hover:text-text-primary hover:bg-bg-tertiary/50'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  );
                }
                if (item.type === 'dropdown') {
                  return (
                    <NavDropdown
                      key={item.id}
                      item={item}
                      isAnyChildActive={isChildActive(item)}
                    />
                  );
                }
                return null;
              })}
            </nav>

            {/* Right side - Status & Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {onToggleAutoRefresh && (
                <button
                  onClick={onToggleAutoRefresh}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                    autoRefreshEnabled
                      ? 'bg-accent-blue/15 text-accent-blue'
                      : 'bg-bg-tertiary text-text-muted'
                  }`}
                >
                  <Timer className="w-4 h-4" />
                  <span className="text-xs font-mono">
                    {autoRefreshEnabled
                      ? `${Math.floor(autoRefreshCountdown / 60)}:${(autoRefreshCountdown % 60)
                          .toString()
                          .padStart(2, '0')}`
                      : 'Paused'}
                  </span>
                </button>
              )}

              {marketClock && (
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    marketClock.isOpen ? 'bg-accent-green/15' : 'bg-accent-red/15'
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      marketClock.isOpen ? 'bg-accent-green animate-pulse' : 'bg-accent-red'
                    }`}
                  />
                  <span
                    className={`text-xs ${
                      marketClock.isOpen ? 'text-accent-green' : 'text-accent-red'
                    }`}
                  >
                    {marketClock.isOpen ? 'Open' : 'Closed'}
                  </span>
                </div>
              )}

              {hasAlpacaKeys && connectionStatus && (
                <button
                  onClick={onShowConnectionModal}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    connectionStatus?.connected ? 'bg-accent-green/15' : 'bg-accent-red/15'
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      connectionStatus?.connected
                        ? 'bg-accent-green animate-pulse'
                        : 'bg-accent-red'
                    }`}
                  />
                  <span
                    className={`text-xs ${
                      connectionStatus?.connected ? 'text-accent-green' : 'text-accent-red'
                    }`}
                  >
                    {connectionStatus?.connected ? 'Connected' : 'Disconnected'}
                  </span>
                </button>
              )}

              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={refreshing}
                  className="btn-secondary p-2 rounded-lg"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              )}

              <button onClick={logout} className="btn-secondary p-2 rounded-lg text-accent-red">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ============ MOBILE HEADER (Top) ============ */}
      <header className="md:hidden sticky top-0 z-40 bg-bg-primary/95 backdrop-blur-xl border-b border-border-color">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg gradient-bg-blue flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-base">{getCurrentPageLabel()}</span>
          </div>

          <div className="flex items-center gap-2">
            {marketClock && (
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  marketClock.isOpen ? 'bg-accent-green animate-pulse' : 'bg-accent-red'
                }`}
                title={marketClock.isOpen ? 'Market Open' : 'Market Closed'}
              />
            )}

            {hasAlpacaKeys && connectionStatus && (
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  connectionStatus?.connected ? 'bg-accent-blue' : 'bg-accent-red'
                }`}
                title={connectionStatus?.connected ? 'Connected' : 'Disconnected'}
              />
            )}

            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className="p-2 rounded-lg bg-bg-tertiary active:bg-bg-secondary"
              >
                <RefreshCw
                  className={`w-5 h-5 ${
                    refreshing ? 'animate-spin text-accent-blue' : 'text-text-secondary'
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Extra header content */}
      {children && (
        <div className="border-b border-border-color bg-bg-secondary/50">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3">{children}</div>
        </div>
      )}

      {/* ============ MOBILE BOTTOM NAVIGATION ============ */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-bg-primary/95 backdrop-blur-xl border-t border-border-color safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {MOBILE_PRIMARY.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 py-2 rounded-xl transition-all ${
                isActive(item.path) ? item.color : 'text-text-muted'
              }`}
            >
              <item.icon
                className={`w-6 h-6 mb-1 ${isActive(item.path) ? 'scale-110' : ''}`}
              />
              <span className="text-[10px] font-medium">{item.label}</span>
              {isActive(item.path) && (
                <div
                  className={`absolute bottom-1 w-1 h-1 rounded-full ${item.color.replace(
                    'text-',
                    'bg-'
                  )}`}
                />
              )}
            </Link>
          ))}

          {/* More Button */}
          <button
            onClick={() => setMoreMenuOpen(!moreMenuOpen)}
            className={`flex flex-col items-center justify-center flex-1 py-2 rounded-xl transition-all ${
              moreMenuOpen ? 'text-accent-blue' : 'text-text-muted'
            }`}
          >
            <MoreHorizontal className="w-6 h-6 mb-1" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* ============ MOBILE "MORE" MENU (Slide-up) ============ */}
      {moreMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
            onClick={() => setMoreMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-bg-secondary rounded-t-3xl animate-slide-up safe-area-bottom"
            style={{ maxHeight: '85vh', overflowY: 'auto' }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-bg-secondary rounded-t-3xl z-10">
              <div className="w-10 h-1 bg-border-color rounded-full" />
            </div>

            {/* User Info */}
            <div className="px-6 py-3 border-b border-border-color">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-accent-blue to-accent-purple flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{user?.username || 'Trader'}</div>
                  <div className="text-xs text-text-muted flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        connectionStatus?.connected ? 'bg-accent-green' : 'bg-accent-red'
                      }`}
                    />
                    {connectionStatus?.connected ? 'Alpaca Connected' : 'Not Connected'}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3 px-6 py-3 border-b border-border-color">
              <div className="bg-bg-tertiary rounded-xl p-2.5 text-center">
                <div
                  className={`text-xs ${
                    marketClock?.isOpen ? 'text-accent-green' : 'text-accent-red'
                  }`}
                >
                  {marketClock?.isOpen ? '🟢 Market Open' : '🔴 Market Closed'}
                </div>
              </div>
              <div className="bg-bg-tertiary rounded-xl p-2.5 text-center">
                <div className="text-xs text-text-secondary">
                  {autoRefreshEnabled
                    ? `⏱️ ${Math.floor(autoRefreshCountdown / 60)}:${(autoRefreshCountdown % 60)
                        .toString()
                        .padStart(2, '0')}`
                    : '⏸️ Paused'}
                </div>
              </div>
            </div>

            {/* Organized Sections */}
            <div className="px-4 py-2">
              {MOBILE_SECTIONS.map((section) => (
                <div key={section.title} className="mb-1">
                  {/* Section Header */}
                  <div className="px-3 pt-3 pb-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                      {section.title}
                    </span>
                  </div>
                  {/* Section Items */}
                  <div className="space-y-0.5">
                    {section.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMoreMenuOpen(false)}
                        className={`flex items-center gap-4 px-3 py-3 rounded-xl transition-all active:bg-bg-tertiary ${
                          isActive(item.path)
                            ? `bg-bg-tertiary ${item.color}`
                            : 'text-text-primary'
                        }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            isActive(item.path) ? 'bg-bg-primary' : 'bg-bg-tertiary'
                          }`}
                        >
                          <item.icon className={`w-5 h-5 ${item.color}`} />
                        </div>
                        <span className="font-medium text-sm">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Logout */}
            <div className="px-4 pb-4 pt-1 border-t border-border-color mt-1">
              <button
                onClick={() => {
                  logout();
                  setMoreMenuOpen(false);
                }}
                className="w-full flex items-center gap-4 px-3 py-3 rounded-xl text-accent-red active:bg-accent-red/10"
              >
                <div className="w-9 h-9 rounded-lg bg-accent-red/10 flex items-center justify-center">
                  <LogOut className="w-5 h-5" />
                </div>
                <span className="font-medium text-sm">Logout</span>
              </button>
            </div>

            {/* Bottom Padding */}
            <div className="h-4" />
          </div>
        </>
      )}

      {/* Spacer for bottom nav on mobile */}
      <div className="md:hidden h-16" />
    </>
  );
};

export default Navigation;
